import React, { useState, useEffect, useCallback, useRef } from 'react';
import { loginAlertAPI } from '../../../services/api';
import * as Icons from 'lucide-react';

const LoginAlertManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // F4 keyboard shortcut to focus search box
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (e.key === 'F4') {
        e.preventDefault();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          setShowDropdown(true);
        }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  // Selected user state
  const [selectedUser, setSelectedUser] = useState(null);
  const [settings, setSettings] = useState(null);
  const [knownIPs, setKnownIPs] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('requests');
  const [saving, setSaving] = useState(false);

  // Recipient Requests state
  const [recipientRequests, setRecipientRequests] = useState([]);
  const [recipientSummary, setRecipientSummary] = useState({ pending: 0, approved: 0, rejected: 0, deleted: 0, total: 0 });
  const [recipientFilter, setRecipientFilter] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [recipientDateFrom, setRecipientDateFrom] = useState('');
  const [recipientDateTo, setRecipientDateTo] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectRemarks, setRejectRemarks] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteRemarks, setDeleteRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(null);

  // Form state
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [otpLoginEnabled, setOtpLoginEnabled] = useState(false);
  const [emails, setEmails] = useState(['']);
  const [mobiles, setMobiles] = useState(['']);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await loginAlertAPI.getAllUsers();
      setUsers(res.data.users || []);
    } catch (err) {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(recipientSearch), 500);
    return () => clearTimeout(timer);
  }, [recipientSearch]);

  const loadRecipientRequests = useCallback(async () => {
    try {
      const params = {};
      if (recipientFilter) params.status = recipientFilter;
      if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
      if (recipientDateFrom) params.date_from = recipientDateFrom;
      if (recipientDateTo) params.date_to = recipientDateTo;
      const res = await loginAlertAPI.getRecipientRequests(params);
      setRecipientRequests(res.data?.requests || []);
      setRecipientSummary(res.data?.summary || { pending: 0, approved: 0, rejected: 0, deleted: 0, total: 0 });
    } catch (err) {
      console.error('Failed to load recipient requests:', err);
    }
  }, [recipientFilter, debouncedSearch, recipientDateFrom, recipientDateTo]);

  useEffect(() => {
    fetchUsers();
    loadRecipientRequests();
  }, [fetchUsers, loadRecipientRequests]);

  const selectUser = async (user) => {
    setSelectedUser(user);
    setActiveTab('settings');
    setError('');
    setSuccess('');
    try {
      const res = await loginAlertAPI.getSettings(user.user_id);
      const data = res.data;
      setSettings(data);
      setAlertsEnabled(data.alerts_enabled);
      setOtpLoginEnabled(data.otp_login_enabled || false);
      const emailValues = (data.emails || []).map(e => e.contact_value);
      setEmails(emailValues.length > 0 ? emailValues : ['']);
      const mobileValues = (data.mobiles || []).map(m => m.contact_value);
      setMobiles(mobileValues.length > 0 ? mobileValues : ['']);
    } catch (err) {
      setError('Failed to load user settings');
    }
  };

  const loadKnownIPs = async () => {
    if (!selectedUser) return;
    try {
      const res = await loginAlertAPI.getKnownIPs(selectedUser.user_id);
      setKnownIPs(res.data.known_ips || []);
    } catch (err) {
      setError('Failed to load known IPs');
    }
  };

  const loadAlertLogs = async () => {
    if (!selectedUser) return;
    try {
      const res = await loginAlertAPI.getLogs(selectedUser.user_id, 100);
      setAlertLogs(res.data.logs || []);
    } catch (err) {
      setError('Failed to load alert logs');
    }
  };

  const handleApproveRequest = async (id) => {
    setActionLoading(id);
    try {
      await loginAlertAPI.approveRecipientRequest(id);
      setSuccess('Request approved successfully');
      loadRecipientRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to approve request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectRequest = async (id) => {
    if (!rejectRemarks.trim()) { setError('Remarks are mandatory for rejection'); return; }
    setActionLoading(id);
    try {
      await loginAlertAPI.rejectRecipientRequest(id, { remarks: rejectRemarks.trim() });
      setSuccess('Request rejected');
      setRejectingId(null);
      setRejectRemarks('');
      loadRecipientRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reject request');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAdminDeleteContact = async (id) => {
    setActionLoading(id);
    try {
      await loginAlertAPI.deleteRecipientContact(id, { remarks: deleteRemarks.trim() || 'Deleted by Admin' });
      setSuccess('Contact deleted by admin');
      setDeletingId(null);
      setDeleteRemarks('');
      loadRecipientRequests();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete contact');
    } finally {
      setActionLoading(null);
    }
  };

  useEffect(() => {
    if (activeTab === 'known_ips') loadKnownIPs();
    if (activeTab === 'logs') loadAlertLogs();
  }, [activeTab, selectedUser]);

  const handleSave = async () => {
    if (!selectedUser) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await loginAlertAPI.updateSettings(selectedUser.user_id, {
        alerts_enabled: alertsEnabled,
        otp_login_enabled: otpLoginEnabled,
        emails: emails.filter(e => e.trim() !== ''),
        mobiles: mobiles.filter(m => m.trim() !== ''),
      });
      setSuccess('Login alert settings saved successfully');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteKnownIP = async (id) => {
    if (!window.confirm('Remove this known IP? The user will receive an alert on next login from this IP.')) return;
    try {
      await loginAlertAPI.deleteKnownIP(id);
      setSuccess('Known IP removed');
      loadKnownIPs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to remove IP');
    }
  };

  const addEmail = () => {
    if (emails.length >= 5) return;
    setEmails([...emails, '']);
  };
  const removeEmail = (idx) => {
    if (emails.length <= 1) { setEmails(['']); return; }
    setEmails(emails.filter((_, i) => i !== idx));
  };
  const updateEmail = (idx, val) => {
    const updated = [...emails];
    updated[idx] = val;
    setEmails(updated);
  };

  const addMobile = () => {
    if (mobiles.length >= 5) return;
    setMobiles([...mobiles, '']);
  };
  const removeMobile = (idx) => {
    if (mobiles.length <= 1) { setMobiles(['']); return; }
    setMobiles(mobiles.filter((_, i) => i !== idx));
  };
  const updateMobile = (idx, val) => {
    const updated = [...mobiles];
    updated[idx] = val;
    setMobiles(updated);
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Reset highlighted index when filtered list changes
  useEffect(() => {
    setHighlightedIdx(-1);
  }, [searchTerm]);

  // Handle keyboard navigation on search input
  const handleSearchKeyDown = (e) => {
    if (!showDropdown && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setShowDropdown(true);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.min(prev + 1, filteredUsers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightedIdx >= 0 && highlightedIdx < filteredUsers.length) {
      e.preventDefault();
      selectUser(filteredUsers[highlightedIdx]);
      setShowDropdown(false);
      setSearchTerm('');
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIdx >= 0 && dropdownRef.current) {
      const items = dropdownRef.current.querySelectorAll('[data-user-item]');
      if (items[highlightedIdx]) {
        items[highlightedIdx].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIdx]);

  const getAlertTypeBadge = (type) => {
    const styles = {
      new_ip: 'bg-blue-100 text-blue-800',
      ip_change: 'bg-yellow-100 text-yellow-800',
      abnormal_login: 'bg-red-100 text-red-800',
      account_lock: 'bg-red-200 text-red-900',
    };
    const labels = {
      new_ip: 'New IP',
      ip_change: 'IP Change',
      abnormal_login: 'Abnormal',
      account_lock: 'Account Lock',
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[type] || 'bg-gray-100 text-gray-800'}`}>
        {labels[type] || type}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-lg">
            <Icons.ShieldAlert className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Login Alert Management</h1>
            <p className="text-sm text-gray-500">Configure email & SMS alerts for user login activity</p>
          </div>
        </div>
        {/* Top-level tabs: Recipient Requests vs Per-User Settings */}
        <div className="flex gap-2">
          <button onClick={() => setActiveTab('requests')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === 'requests' ? 'bg-orange-100 text-orange-700 font-bold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            <Icons.Bell className="w-4 h-4" />
            Recipient Requests
            {recipientSummary.pending > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{recipientSummary.pending}</span>
            )}
          </button>
          <button onClick={() => setActiveTab('per_user')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab !== 'requests' && activeTab !== 'per_user' ? 'bg-blue-100 text-blue-700 font-bold' :
              activeTab === 'per_user' ? 'bg-blue-100 text-blue-700 font-bold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            <Icons.Users className="w-4 h-4" />
            Per-User Settings
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icons.AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError('')} className="ml-auto"><Icons.X className="w-4 h-4" /></button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Icons.CheckCircle className="w-5 h-5" />
          {success}
        </div>
      )}

      {/* ═══════ Recipient Requests Tab ═══════ */}
      {activeTab === 'requests' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {/* Summary Cards */}
          <div className="p-4 border-b border-gray-200">
            <div className="grid grid-cols-5 gap-3">
              {[
                { label: 'Total', value: recipientSummary.total, color: 'bg-gray-100 text-gray-700', filter: '' },
                { label: 'Pending', value: recipientSummary.pending, color: 'bg-amber-100 text-amber-700', filter: 'pending' },
                { label: 'Approved', value: recipientSummary.approved, color: 'bg-green-100 text-green-700', filter: 'approved' },
                { label: 'Rejected', value: recipientSummary.rejected, color: 'bg-red-100 text-red-700', filter: 'rejected' },
                { label: 'Deleted', value: recipientSummary.deleted || 0, color: 'bg-gray-200 text-gray-600', filter: 'deleted' },
              ].map(s => (
                <button key={s.label} onClick={() => { setRecipientFilter(s.filter); }}
                  className={`p-3 rounded-lg text-center transition-all ${recipientFilter === s.filter ? 'ring-2 ring-blue-400 shadow-sm' : ''} ${s.color}`}>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className="text-xs font-medium mt-0.5">{s.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                  placeholder="Search by Request #, User, Mobile, Email..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:border-blue-400" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 font-medium">From:</label>
                <input type="date" value={recipientDateFrom} onChange={e => setRecipientDateFrom(e.target.value)}
                  className="px-2 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-400" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 font-medium">To:</label>
                <input type="date" value={recipientDateTo} onChange={e => setRecipientDateTo(e.target.value)}
                  className="px-2 py-2 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-blue-400" />
              </div>
              {(recipientSearch || recipientDateFrom || recipientDateTo || recipientFilter) && (
                <button onClick={() => { setRecipientSearch(''); setRecipientDateFrom(''); setRecipientDateTo(''); setRecipientFilter(''); }}
                  className="px-3 py-2 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-1">
                  <Icons.X className="w-3 h-3" /> Clear
                </button>
              )}
            </div>
          </div>

          {/* Requests Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Request #</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">User</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Type</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Contact</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Requested</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Status</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Actioned By</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Action Date</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs">Remarks</th>
                  <th className="px-4 py-3 font-medium text-gray-600 text-xs text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {recipientRequests.length === 0 ? (
                  <tr><td colSpan="10" className="text-center py-8 text-gray-400">
                    <Icons.Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>No recipient requests found</p>
                  </td></tr>
                ) : recipientRequests.map(req => (
                  <React.Fragment key={req.id}>
                    <tr className={`border-b border-gray-100 hover:bg-gray-50 ${
                      req.status === 'pending' ? 'bg-amber-50/30' : req.status === 'deleted' ? 'bg-gray-50/50 opacity-70' : ''
                    }`}>
                      <td className="px-4 py-2.5 font-mono text-xs text-indigo-600 font-bold">{req.request_number}</td>
                      <td className="px-4 py-2.5">
                        <div className="text-xs font-medium text-gray-900">{req.username}</div>
                        <div className="text-[10px] text-gray-400">{req.full_name || `ID: ${req.user_id}`}</div>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          req.contact_type === 'email' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {req.contact_type === 'email' ? <Icons.Mail className="w-2.5 h-2.5" /> : <Icons.Smartphone className="w-2.5 h-2.5" />}
                          {req.contact_type === 'email' ? 'Email' : 'Mobile'}
                        </span>
                      </td>
                      <td className={`px-4 py-2.5 font-mono text-xs font-semibold ${req.status === 'deleted' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>{req.contact_value}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{req.requested_at}</td>
                      <td className="px-4 py-2.5">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          req.status === 'approved' ? 'bg-green-100 text-green-700' :
                          req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          req.status === 'deleted' ? 'bg-gray-200 text-gray-600' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {req.status === 'pending' ? 'Pending Approval' :
                           req.status === 'deleted' ? 'Deleted by Admin' :
                           req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-gray-500">{req.actioned_by_name || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">{req.actioned_at || '—'}</td>
                      <td className="px-4 py-2.5 text-xs text-gray-400 max-w-[150px] truncate" title={req.admin_remarks}>{req.admin_remarks || '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {req.status === 'pending' && (
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => handleApproveRequest(req.id)} disabled={actionLoading === req.id}
                              className="px-2.5 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 disabled:opacity-50 flex items-center gap-1">
                              {actionLoading === req.id ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.CheckCircle className="w-3 h-3" />}
                              Approve
                            </button>
                            <button onClick={() => { setRejectingId(req.id); setRejectRemarks(''); }}
                              className="px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 border border-red-200 rounded hover:bg-red-100 flex items-center gap-1">
                              <Icons.XCircle className="w-3 h-3" /> Reject
                            </button>
                          </div>
                        )}
                        {(req.status === 'approved' || req.status === 'rejected') && req.status !== 'deleted' && (
                          <button onClick={() => { setDeletingId(req.id); setDeleteRemarks(''); }}
                            className="px-2.5 py-1 text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200 rounded hover:bg-red-50 hover:text-red-600 hover:border-red-200 flex items-center gap-1 mx-auto">
                            <Icons.Trash2 className="w-3 h-3" /> Delete
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Inline reject remarks input */}
                    {rejectingId === req.id && (
                      <tr className="bg-red-50/50">
                        <td colSpan="10" className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icons.MessageSquare className="w-4 h-4 text-red-400 flex-shrink-0" />
                            <input type="text" value={rejectRemarks} onChange={e => setRejectRemarks(e.target.value)}
                              placeholder="Enter rejection reason (mandatory)..."
                              className="flex-1 px-3 py-2 border border-red-200 rounded-lg text-sm focus:ring-2 focus:ring-red-300 focus:border-red-400"
                              autoFocus />
                            <button onClick={() => handleRejectRequest(req.id)} disabled={actionLoading === req.id || !rejectRemarks.trim()}
                              className="px-4 py-2 text-xs font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg disabled:opacity-50 flex items-center gap-1">
                              {actionLoading === req.id ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.XCircle className="w-3 h-3" />}
                              Confirm Reject
                            </button>
                            <button onClick={() => { setRejectingId(null); setRejectRemarks(''); }}
                              className="px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Inline delete remarks input */}
                    {deletingId === req.id && (
                      <tr className="bg-gray-50/80">
                        <td colSpan="10" className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Icons.Trash2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <input type="text" value={deleteRemarks} onChange={e => setDeleteRemarks(e.target.value)}
                              placeholder="Reason for deletion (optional)..."
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gray-300"
                              autoFocus />
                            <button onClick={() => handleAdminDeleteContact(req.id)} disabled={actionLoading === req.id}
                              className="px-4 py-2 text-xs font-bold text-white bg-gray-600 hover:bg-gray-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                              {actionLoading === req.id ? <Icons.Loader2 className="w-3 h-3 animate-spin" /> : <Icons.Trash2 className="w-3 h-3" />}
                              Confirm Delete
                            </button>
                            <button onClick={() => { setDeletingId(null); setDeleteRemarks(''); }}
                              className="px-3 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-3 border-t border-gray-100 flex items-center justify-between">
            <button onClick={loadRecipientRequests} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
              <Icons.RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <span className="text-xs text-gray-400">{recipientRequests.length} request(s)</span>
          </div>
        </div>
      )}

      {/* ═══════ Per-User Settings Tab ═══════ */}
      {activeTab !== 'requests' && (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User List Panel */}
        <div className="lg:col-span-1 bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200 relative">
            <div className="relative">
              <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search users... (F4)"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onKeyDown={handleSearchKeyDown}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {showDropdown && searchTerm && filteredUsers.length > 0 && (
              <div ref={dropdownRef} className="absolute z-50 left-4 right-4 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {filteredUsers.slice(0, 20).map((user, idx) => (
                  <div
                    key={user.user_id}
                    data-user-item
                    onClick={() => { selectUser(user); setShowDropdown(false); setSearchTerm(''); }}
                    className={`px-3 py-2 cursor-pointer text-sm flex items-center justify-between ${
                      idx === highlightedIdx ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'
                    } ${selectedUser?.user_id === user.user_id ? 'font-semibold' : ''}`}
                  >
                    <div>
                      <span className="font-medium">{user.username}</span>
                      <span className="text-gray-400 ml-2">{user.full_name || user.email}</span>
                    </div>
                    {user.alerts_enabled && <Icons.Bell className="w-3 h-3 text-green-500" />}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <Icons.Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No users found</div>
            ) : (
              filteredUsers.map((user, idx) => (
                <div
                  key={user.user_id}
                  data-user-item
                  onClick={() => selectUser(user)}
                  className={`p-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedUser?.user_id === user.user_id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm text-gray-900">{user.username}</div>
                      <div className="text-xs text-gray-500">{user.full_name || user.email}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {user.alerts_enabled ? (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full text-[10px] font-medium">
                          <Icons.Bell className="w-3 h-3" /> Alerts: ON
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full text-[10px] font-medium">
                          <Icons.BellOff className="w-3 h-3" /> Alerts: OFF
                        </span>
                      )}
                      {user.otp_login_enabled ? (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-violet-100 text-violet-700 rounded-full text-[10px] font-medium">
                          <Icons.KeyRound className="w-3 h-3" /> OTP: ON
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-400 rounded-full text-[10px] font-medium">
                          <Icons.KeyRound className="w-3 h-3" /> OTP: OFF
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span className="flex items-center gap-1"><Icons.Mail className="w-3 h-3" /> {user.email_count}</span>
                    <span className="flex items-center gap-1"><Icons.Smartphone className="w-3 h-3" /> {user.sms_count}</span>
                    <span className="flex items-center gap-1"><Icons.Globe className="w-3 h-3" /> {user.known_ip_count} IPs</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Settings Panel */}
        <div className="lg:col-span-2">
          {!selectedUser ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Icons.UserCog className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a user to manage their login alert settings</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {/* User Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Icons.User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{selectedUser.username}</div>
                    <div className="text-sm text-gray-500">{selectedUser.full_name} &bull; {selectedUser.email}</div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-gray-200">
                {[
                  { key: 'settings', label: 'Alert Settings', icon: Icons.Settings },
                  { key: 'known_ips', label: 'Known IPs', icon: Icons.Globe },
                  { key: 'logs', label: 'Alert History', icon: Icons.History },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.key
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="p-6">
                {/* Settings Tab */}
                {activeTab === 'settings' && (
                  <div className="space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <Icons.ShieldCheck className="w-5 h-5 text-blue-600" />
                        <div>
                          <div className="font-medium text-gray-900">Login Alerts</div>
                          <div className="text-sm text-gray-500">Send alerts when new IP / device is detected</div>
                        </div>
                      </div>
                      <button
                        onClick={() => setAlertsEnabled(!alertsEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          alertsEnabled ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          alertsEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* OTP Login Toggle */}
                    <div className="flex items-center justify-between p-4 bg-violet-50 rounded-lg border border-violet-100">
                      <div className="flex items-center gap-3">
                        <Icons.KeyRound className="w-5 h-5 text-violet-600" />
                        <div>
                          <div className="font-medium text-gray-900">OTP Login Verification</div>
                          <div className="text-sm text-gray-500">Require mobile OTP code after password verification</div>
                          {otpLoginEnabled && (
                            <div className="text-xs text-violet-600 mt-1">
                              {mobiles.filter(m => m.trim()).length > 0
                                ? `${mobiles.filter(m => m.trim()).length} SMS contact(s) + user mobile will receive OTP`
                                : 'OTP will be sent to user\'s registered mobile number'}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => setOtpLoginEnabled(!otpLoginEnabled)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          otpLoginEnabled ? 'bg-violet-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          otpLoginEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`} />
                      </button>
                    </div>

                    {/* Email Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Icons.Mail className="w-4 h-4" /> Email Addresses (max 5)
                        </label>
                        {emails.length < 5 && (
                          <button onClick={addEmail} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                            <Icons.Plus className="w-3 h-3" /> Add Email
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {emails.map((email, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => updateEmail(idx, e.target.value)}
                              placeholder="email@example.com"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button onClick={() => removeEmail(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                              <Icons.Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Mobile Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                          <Icons.Smartphone className="w-4 h-4" /> Mobile Numbers for SMS (max 5)
                        </label>
                        {mobiles.length < 5 && (
                          <button onClick={addMobile} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                            <Icons.Plus className="w-3 h-3" /> Add Mobile
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {mobiles.map((mobile, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="tel"
                              value={mobile}
                              onChange={(e) => updateMobile(idx, e.target.value)}
                              placeholder="919876543210"
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button onClick={() => removeMobile(idx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                              <Icons.Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="text-xs text-gray-400 bg-gray-50 p-3 rounded-lg">
                      <strong>Alert triggers:</strong> New IP login, IP change, abnormal login activity, account lock.
                      <br />SMS alerts use DLT Template ID: <code className="bg-gray-200 px-1 rounded">1007203650183435750</code>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end">
                      <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-sm"
                      >
                        {saving ? <Icons.Loader2 className="w-4 h-4 animate-spin" /> : <Icons.Save className="w-4 h-4" />}
                        {saving ? 'Saving...' : 'Save Settings'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Known IPs Tab */}
                {activeTab === 'known_ips' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <p className="text-sm text-gray-500">
                          New IPs are recorded as <span className="text-amber-600 font-medium">untrusted</span> on first login. Admin must verify and mark as trusted.
                        </p>
                        <p className="text-xs text-gray-400 mt-1">Only trusted IPs bypass login alerts. Untrusted IPs trigger alerts on every login.</p>
                      </div>
                      <button onClick={loadKnownIPs} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <Icons.RefreshCw className="w-3 h-3" /> Refresh
                      </button>
                    </div>
                    {knownIPs.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Icons.Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No known IPs yet. IPs will be recorded on login.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left">
                              <th className="px-4 py-2 font-medium text-gray-600">IP Address</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Location</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Device</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Status</th>
                              <th className="px-4 py-2 font-medium text-gray-600">First Seen</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Last Seen</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Logins</th>
                              <th className="px-4 py-2 font-medium text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {knownIPs.map(ip => (
                              <tr key={ip.id} className={`border-b border-gray-100 hover:bg-gray-50 ${!ip.is_trusted ? 'bg-amber-50/50' : ''}`}>
                                <td className="px-4 py-2 font-mono text-xs">{ip.ip_address}</td>
                                <td className="px-4 py-2 text-xs">{ip.location || '-'}</td>
                                <td className="px-4 py-2 text-xs max-w-[200px] truncate" title={ip.device_info}>{ip.device_info || '-'}</td>
                                <td className="px-4 py-2">
                                  {ip.is_trusted ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                      <Icons.ShieldCheck className="w-3 h-3" /> Trusted
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                      <Icons.ShieldAlert className="w-3 h-3" /> Untrusted
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-xs text-gray-500">{ip.first_seen_at}</td>
                                <td className="px-4 py-2 text-xs text-gray-500">{ip.last_seen_at}</td>
                                <td className="px-4 py-2 text-center">{ip.login_count}</td>
                                <td className="px-4 py-2">
                                  <div className="flex items-center gap-1">
                                    {!ip.is_trusted ? (
                                      <button
                                        onClick={async () => {
                                          try { await loginAlertAPI.trustKnownIP(ip.id); setSuccess('IP marked as trusted'); loadKnownIPs(); setTimeout(() => setSuccess(''), 3000); }
                                          catch (e) { setError('Failed to trust IP'); }
                                        }}
                                        className="px-2 py-1 text-xs font-medium bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100"
                                        title="Verify and trust this IP"
                                      >
                                        <Icons.CheckCircle className="w-3.5 h-3.5" />
                                      </button>
                                    ) : (
                                      <button
                                        onClick={async () => {
                                          try { await loginAlertAPI.untrustKnownIP(ip.id); setSuccess('IP marked as untrusted'); loadKnownIPs(); setTimeout(() => setSuccess(''), 3000); }
                                          catch (e) { setError('Failed to untrust IP'); }
                                        }}
                                        className="px-2 py-1 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100"
                                        title="Mark as untrusted (will trigger alerts)"
                                      >
                                        <Icons.ShieldAlert className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleDeleteKnownIP(ip.id)}
                                      className="px-2 py-1 text-xs text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                                      title="Remove (will trigger alert on next login)"
                                    >
                                      <Icons.Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Alert History Tab */}
                {activeTab === 'logs' && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-500">Recent login alert history</p>
                      <button onClick={loadAlertLogs} className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1">
                        <Icons.RefreshCw className="w-3 h-3" /> Refresh
                      </button>
                    </div>
                    {alertLogs.length === 0 ? (
                      <div className="text-center py-8 text-gray-400">
                        <Icons.History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No alerts sent yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-50 text-left">
                              <th className="px-3 py-2 font-medium text-gray-600">Time</th>
                              <th className="px-3 py-2 font-medium text-gray-600">Type</th>
                              <th className="px-3 py-2 font-medium text-gray-600">Channel</th>
                              <th className="px-3 py-2 font-medium text-gray-600">Sent To</th>
                              <th className="px-3 py-2 font-medium text-gray-600">IP</th>
                              <th className="px-3 py-2 font-medium text-gray-600">Location</th>
                              <th className="px-3 py-2 font-medium text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {alertLogs.map(log => (
                              <tr key={log.id} className="border-b border-gray-100 hover:bg-gray-50">
                                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{log.created_at}</td>
                                <td className="px-3 py-2">{getAlertTypeBadge(log.alert_type)}</td>
                                <td className="px-3 py-2">
                                  <span className={`flex items-center gap-1 text-xs ${log.alert_channel === 'email' ? 'text-purple-600' : 'text-green-600'}`}>
                                    {log.alert_channel === 'email' ? <Icons.Mail className="w-3 h-3" /> : <Icons.Smartphone className="w-3 h-3" />}
                                    {log.alert_channel.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-xs font-mono">{log.contact_value}</td>
                                <td className="px-3 py-2 text-xs font-mono">{log.ip_address}</td>
                                <td className="px-3 py-2 text-xs">{log.location || '-'}</td>
                                <td className="px-3 py-2">
                                  {log.status === 'sent' ? (
                                    <span className="flex items-center gap-1 text-green-600 text-xs">
                                      <Icons.CheckCircle className="w-3 h-3" /> Sent
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-red-600 text-xs" title={log.error_message}>
                                      <Icons.XCircle className="w-3 h-3" /> Failed
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      )}
    </div>
  );
};

export default LoginAlertManagement;
